import {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { deepPurple } from "@mui/material/colors";
import "./Navbar.css";
import VylabsIcon from "../../Assets/Images/VybogTAL-Logo.svg";
import vybogTALDarkLogo from "../../Assets/Images/VybogTAL-Logo-Dark.svg";
import VylabsAloneIcon from "../../Assets/Images/VybogTAL-Icon.svg";
import DashboardIcon from "../../Assets/Images/dashboard-icon.svg";
import DashboardDarkIcon from "../../Assets/Images/dashboard-dark-icon.svg";
import DashboardActiveIcon from "../../Assets/Images/active-dashboard-icon.svg";
import AIIcon from "../../Assets/Images/convo-menu-icon.svg";
import AIDarkIcon from "../../Assets/Images/convo-menu-dark-icon.svg";
import AIActiveIcon from "../../Assets/Images/convo-menu-active-icon.svg";
import invoiceActive from "../../Assets/Images/invoice-active-icons.svg";
import TabOpenIcon from "../../Assets/Images/tab-open-burger-icon.svg";
import TabOpenDarkIcon from "../../Assets/Images/tab-open-burger-dark-icon.svg";
import TabCloseIcon from "../../Assets/Images/tab-close-icon.svg";
import TabCloseDarkIcon from "../../Assets/Images/tab-close-dark-icon.svg";
import SettingsIcon from "../../Assets/Images/mingcute_settings-1-line.svg";
import SettingsDarkIcon from "../../Assets/Images/settings-dark.svg";
import LogoutIcon from "../../Assets/Images/logout-icon.png";
import LogoutDarkIcon from "../../Assets/Images/logout-dark-icon.png";
import invoiceLightIcon from "../../Assets/Images/invoice-inactive-icon-light.svg";
import invoiceIcon from "../../Assets/Images/invoice-nav-icon-dark.svg";
import invoiceDarkIcon from "../../Assets/Images/invoice-inactive-icon-dark.svg";
import candidateIcon from "../../Assets/Images/candidate-icon.svg";
import candidateDarkIcon from "../../Assets/Images/candidate-dark-icon.svg";
import ActiveCandidateIcon from "../../Assets/Images/active-candidate-icon.svg";
import JobsIcon from "../../Assets/Images/jobs-icon.svg";
import JobsDarkIcon from "../../Assets/Images/job-dark-icon.svg";
import ActiveJobsIcon from "../../Assets/Images/active-jobs-icon.svg";
import ClientsIcon from "../../Assets/Images/client-menu-icon.svg";
import ClientsDarkIcon from "../../Assets/Images/client-menu-icon-dark.svg";
import ActiveClientsIcon from "../../Assets/Images/client-menu-icon-active.svg";
import scheduleIcon from "../../Assets/Images/scheduleIcon.svg";
import scheduleDarkIcon from "../../Assets/Images/scheduleDark-Icon.svg";
import activeScheduleIcon from "../../Assets/Images/activeScheduleIcon.svg";
import { DynamicContext } from "../../Context/DynamicContext";
import NestedList from "../../Common/Nested-List/NestedList";
import DropDownMenu from "../../Common/Drop-Down-Menu/DropDownMenu";
import {
  applyEllipsis,
  handleLogoutCommon,
} from "../../Common/Common-Function/CommonFunctions";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import feedbackIcon from "../../Assets/Images/feedback-black-outline.svg";
import feedbackDarkIcon from "../../Assets/Images/feedback-white-outline.svg";
import vendorIcon from "../../Assets/Images/vendor-icon.svg";
import vendorDarkIcon from "../../Assets/Images/vendor-dark-icon.svg";
import vendorActiveIcon from "../../Assets/Images/vendor-icon-active.svg";
import RPActiveIcon from "../../Assets/Images/RP Icon - Active.svg";
import RPDarkIcon from "../../Assets/Images/RP Icon - Dark.svg";
import RPLightIcon from "../../Assets/Images/RP Icon - Light.svg";
import reportIcon from "../../Assets/Images/report-icon-inactive.svg";
import reportDarkIcon from "../../Assets/Images/report-icon-dark.svg";
import reportActiveIcon from "../../Assets/Images/report-icon-active.svg";
import html2canvas from "html2canvas";
import { GET_RECRUITER_BY_ID, VYLABS_BASE_URL } from "../../helper/url_helper";
import { get } from "../../helper/Api_helper";

const PROFILE_IMAGE_REFRESH_INTERVAL_MS = 59 * 60 * 1000;

function Navbar() {
  const {
    isDarkMode,
    closeTab,
    setCloseTab = () => {},
    userDetails,
    setUserDetails,
    setUploadResumeViewOpen,
    setOpenFeedBack,
    setFeedbackScreenshot,
    showUploadInterruptPopup,
    setPendingNavigationPath,
    hasSubscription,
    isVendorFeatureEnabled,
    setShowResumeParser,
    setUploadedResumes,
    setIncompleteFiles,
    setParsedData,
    setIncompleteData,
    setRunSetId,
    setErrorMessages,
    setSuccessMessages,
    setShowFailedTab,
    setIsParsingComplete,
    AdminAccess,
    UnAssociatedAccess,
    SuperAdminAccess,
    isHiringTeamTenant
  } = useContext(DynamicContext);
  const navigate = useNavigate();
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [activeLink, setActiveLink] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const location = useLocation();
  const footerMenuRef = useRef(null);
  const lastWidthRef = useRef(window.innerWidth);

  useEffect(() => {
    let isRefreshing = false;

    const refreshProfileImage = async () => {
      if (isRefreshing || !userDetails?.id) return;

      isRefreshing = true;
      try {
        const response = await get(
          VYLABS_BASE_URL,
          `${GET_RECRUITER_BY_ID}?id=${userDetails.id}`,
        );
        if (response?.user) {
          setUserDetails(response.user);
        }
      } catch (error) {
        console.error("Failed to refresh navbar profile image:", error);
      } finally {
        isRefreshing = false;
      }
    };

    const refreshInterval = window.setInterval(
      refreshProfileImage,
      PROFILE_IMAGE_REFRESH_INTERVAL_MS,
    );

    return () => window.clearInterval(refreshInterval);
  }, [setUserDetails, userDetails?.id]);

  const menuItems = useMemo(
    () => [
      {
        name: "Dashboard",
        path: "/dashboard",
        icon: isDarkMode ? DashboardDarkIcon : DashboardIcon,
        activeIcon: isDarkMode ? DashboardDarkIcon : DashboardActiveIcon,
        hoverIcon: isDarkMode ? DashboardDarkIcon : DashboardActiveIcon,
      },
      {
        name: "Daily Reports",
        path: "/daily-reports",
        icon: isDarkMode ? reportDarkIcon : reportIcon,
        activeIcon: isDarkMode ? reportDarkIcon : reportActiveIcon,
        hoverIcon: isDarkMode ? reportDarkIcon : reportActiveIcon,
      },
      {
        name: "Resume Parser",
        path: "/resume-parser",
        icon: isDarkMode ? RPDarkIcon : RPLightIcon,
        activeIcon: isDarkMode ? RPDarkIcon : RPActiveIcon,
        hoverIcon: isDarkMode ? RPDarkIcon : RPActiveIcon,
      },
      {
        name: "Candidates",
        path: "/candidates-list",
        icon: isDarkMode ? candidateDarkIcon : candidateIcon,
        activeIcon: isDarkMode ? candidateDarkIcon : ActiveCandidateIcon,
        hoverIcon: isDarkMode ? candidateDarkIcon : ActiveCandidateIcon,
      },
      {
        name: "Jobs",
        path: "/jobs-list",
        icon: isDarkMode ? JobsDarkIcon : JobsIcon,
        activeIcon: isDarkMode ? JobsDarkIcon : ActiveJobsIcon,
        hoverIcon: isDarkMode ? JobsDarkIcon : ActiveJobsIcon,
      },
      {
        name: "Interviews",
        path: "/interview-schdule",
        icon: isDarkMode ? scheduleDarkIcon : scheduleIcon,
        activeIcon: isDarkMode ? scheduleDarkIcon : activeScheduleIcon,
        hoverIcon: isDarkMode ? scheduleDarkIcon : activeScheduleIcon,
      },
      {
        name: isHiringTeamTenant ? "Hiring Team" : "Clients",
        path: "/clients-list",
        icon: isDarkMode ? ClientsDarkIcon : ClientsIcon,
        activeIcon: isDarkMode ? ClientsDarkIcon : ActiveClientsIcon,
        hoverIcon: isDarkMode ? ClientsDarkIcon : ActiveClientsIcon,
      },
      ...(isVendorFeatureEnabled
        ? [
            {
              name: "Vendors",
              path: "/vendors",
              icon: isDarkMode ? vendorDarkIcon : vendorIcon,
              activeIcon: isDarkMode ? vendorDarkIcon : vendorActiveIcon,
              hoverIcon: isDarkMode ? vendorDarkIcon : vendorIcon,
            },
          ]
        : []),
      {
        name: "Conversations",
        path: "/conversations",
        icon: isDarkMode ? AIDarkIcon : AIIcon,
        activeIcon: isDarkMode ? AIDarkIcon : AIActiveIcon,
        hoverIcon: isDarkMode ? AIDarkIcon : AIActiveIcon,
      },
      ...(isHiringTeamTenant
        ? []
        : [
            {
              name: "Invoices",
              path: "/invoice-list",
              icon: isDarkMode ? invoiceDarkIcon : invoiceLightIcon,
              activeIcon: isDarkMode ? invoiceDarkIcon : invoiceActive,
              hoverIcon: isDarkMode ? invoiceDarkIcon : invoiceActive,
            },
          ]),

      // {
      //   name: "Notifications",
      //   path: "/notifications",
      //   icon: isDarkMode ? NotificationDarkIcon : NotificationIcon,
      //   activeIcon: isDarkMode ? NotificationDarkIcon : NotificationActiveIcon,
      //   hoverIcon: isDarkMode ? NotificationDarkIcon : NotificationActiveIcon,
      // },
      // {
      //   name: "Dice Search",
      //   path: "/dice-search",
      //   icon: isDarkMode ? diceDarkIcon : diceIcon,
      //   activeIcon: isDarkMode ? diceDarkIcon : diceActiveIcon,
      //   hoverIcon: isDarkMode ? diceDarkIcon : diceActiveIcon,
      // },
      // {
      //   name: "Monster Search",
      //   path: "/monster-search",
      //   icon: isDarkMode ? AIDarkIcon : AIIcon,
      //   activeIcon: isDarkMode ? AIDarkIcon : AIActiveIcon,
      //   hoverIcon: isDarkMode ? AIDarkIcon : AIActiveIcon,
      // },
    ],
    [isDarkMode, isVendorFeatureEnabled, isHiringTeamTenant],
  );

  const filteredMenuItems = useMemo(() => {
    if (UnAssociatedAccess) {
      return menuItems.filter((item) => item.name === "Dashboard");
    }
    let filtered = menuItems;
    filtered = filtered.filter((item) => {
      if (item.name === "Clients") {
        return AdminAccess || SuperAdminAccess;
      }

      if (item.name === "Vendors") {
        return isVendorFeatureEnabled;
      }

      if (item.name === "Invoices") {
        return AdminAccess || SuperAdminAccess;
      }

      return true;
    });

    if (!hasSubscription) {
      filtered = filtered.filter(
        (item) => item.name === "Dashboard" || item.name === "Settings",
      );
    }

    return filtered;
  }, [
    menuItems,
    UnAssociatedAccess,
    hasSubscription,
    isVendorFeatureEnabled,
    AdminAccess,
  ]);

  const handleLogout = useCallback(() => {
    handleLogoutCommon({
      setPendingNavigationPath,
      setMobileMenuOpen,
      setPopupVisible,
      setUserDetails,
      setShowResumeParser,
      setIsParsingComplete,
      setUploadedResumes,
      setIncompleteFiles,
      setParsedData,
      setIncompleteData,
      setRunSetId,
      setErrorMessages,
      setSuccessMessages,
      setShowFailedTab,
    });
  }, [
    setPendingNavigationPath,
    setMobileMenuOpen,
    setPopupVisible,
    setUserDetails,
    setShowResumeParser,
    setIsParsingComplete,
    setUploadedResumes,
    setIncompleteFiles,
    setParsedData,
    setIncompleteData,
    setRunSetId,
    setErrorMessages,
    setSuccessMessages,
    setShowFailedTab,
  ]);

  const openSettings = useCallback(() => {
    setPendingNavigationPath("/settings/profile");
    setMobileMenuOpen(false);
    setPopupVisible(false);
    navigate("/settings/profile");
    setMobileMenuOpen(false);
    setPopupVisible(false);
  }, [setPendingNavigationPath, setMobileMenuOpen, setPopupVisible, navigate]);

  const openProvideFeedback = useCallback(() => {
    setPopupVisible(true);
    setMobileMenuOpen(false);

    setPendingNavigationPath("/feedback");
    const contentElement = document.body;
    if (!contentElement) {
      console.error("Content not found for screenshot.");
      return;
    }

    const hideSelectors = [
      ".desktop-avatar-dropdown",
      ".mobile-avatar-dropdown",
      ".desktop-avatar-dropdown .MuiPaper-root",
      ".mobile-avatar-dropdown .MuiPaper-root",
      ".MuiSpeedDialAction-root",
      ".MuiTooltip-popper",
      ".speedDial-popup-icon",
      ".dropdown-menu-item",
    ];

    const textsToHide = ["Settings", "Provide Feedback", "Logout"];
    const hiddenNodes = [];

    const hideNode = (el) => {
      if (!el) return;
      hiddenNodes.push({ el, origVisibility: el.style.visibility || "" });
      el.style.visibility = "hidden";
    };

    try {
      hideSelectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          if (
            el.classList &&
            (el.classList.contains("footer-menu") ||
              el.classList.contains("closed-footer-menu") ||
              el.classList.contains("mobile-footer-menu"))
          ) {
            return;
          }
          hideNode(el);
        });
      });

      document.querySelectorAll("body *").forEach((el) => {
        try {
          if (el.children.length === 0) {
            const t = (el.textContent || "").trim();
            if (textsToHide.includes(t)) {
              hideNode(el);
            }
          }
          if (el.tagName === "IMG") {
            const alt = (el.getAttribute("alt") || "").toLowerCase();
            if (
              alt.includes("settings") ||
              alt.includes("feedback") ||
              alt.includes("logout")
            ) {
              hideNode(el);
            }
          }
        } catch (e) {}
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          html2canvas(contentElement, {
            useCORS: true,
            allowTaint: true,
            ignoreElements: (element) => {
              const bgImage = window.getComputedStyle(element).backgroundImage;
              if (
                bgImage &&
                bgImage !== "none" &&
                bgImage.includes("undefined")
              ) {
                return true;
              }
              if (
                element.classList &&
                (element.classList.contains("MuiSpeedDial-root") ||
                  element.classList.contains("MuiSpeedDial-fab") ||
                  element.classList.contains("MuiSpeedDialAction-fab"))
              ) {
                return true;
              }
              return false;
            },
          })
            .then((canvas) => {
              const screenshot = canvas.toDataURL("image/png");
              setFeedbackScreenshot(screenshot);
              setOpenFeedBack(true);
              setMobileMenuOpen(false);
              setPopupVisible(false);
            })
            .catch((error) => {
              console.error("html2canvas error:", error);
            })
            .finally(() => {
              hiddenNodes.forEach(({ el, origVisibility }) => {
                el.style.visibility = origVisibility;
              });
            });
        });
      });
    } catch (err) {
      console.error("openProvideFeedback error:", err);
      hiddenNodes.forEach(({ el, origVisibility }) => {
        el.style.visibility = origVisibility;
      });
    }
  }, [
    setPendingNavigationPath,
    setFeedbackScreenshot,
    setOpenFeedBack,
    setMobileMenuOpen,
    setPopupVisible,
  ]);

  const popupItems = useMemo(() => {
    return [
      {
        name: "Settings",
        icon: isDarkMode ? SettingsDarkIcon : SettingsIcon,
        eventHandler: openSettings,
        speedDialIcon: (
          <img
            src={SettingsIcon}
            alt="SettingsIcon"
            className="speedDial-popup-icon"
          />
        ),
      },
      {
        name: "Feedback",
        icon: isDarkMode ? feedbackDarkIcon : feedbackIcon,
        eventHandler: openProvideFeedback,
        speedDialIcon: (
          <img
            src={feedbackIcon}
            alt="Feedback"
            className="speedDial-popup-icon"
          />
        ),
      },
      {
        name: "Logout",
        icon: isDarkMode ? LogoutDarkIcon : LogoutIcon,
        eventHandler: handleLogout,
        speedDialIcon: (
          <img src={LogoutIcon} alt="Logout" className="speedDial-popup-icon" />
        ),
      },
    ];
  }, [isDarkMode, openSettings, openProvideFeedback, handleLogout]);

  function handleLinkClick(name, path) {
    proceedToNavigate(name, path);
  }

  function proceedToNavigate(name, path) {
    // if (name === "Resume Parser") setShowResumeParser(false);
    if (name === "Candidates" || name === "/candidates-list")
      setUploadResumeViewOpen(false);
    navigate(path);
    setMobileMenuOpen(false);
    setPopupVisible(false);
    setActiveLink(name);
  }

  function toggleMobileMenu() {
    setMobileMenuOpen(!mobileMenuOpen);
    if (isPopupVisible) setPopupVisible(false);
  }

  const togglePopup = useCallback(
    (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      if (showUploadInterruptPopup) {
        setPopupVisible(false);
        return;
      }
      setPopupVisible((prev) => !prev);
    },
    [setPopupVisible, showUploadInterruptPopup],
  );

  function handleTabChange() {
    const currentWidth = window.innerWidth;
    setCloseTab((prev) => {
      const newState = !prev;
      localStorage.setItem("closeTab", JSON.stringify(newState));
      if (currentWidth > 768 && currentWidth < 992) {
        if (!newState) {
          sessionStorage.setItem("manualOpenInMidResolution", "true");
        } else {
          sessionStorage.removeItem("manualOpenInMidResolution");
        }
      }
      return newState;
    });
  }

  useEffect(() => {
    const currentWidth = window.innerWidth;
    setIsMobileView(currentWidth <= 768);

    const savedState = localStorage.getItem("closeTab");
    const manualOpen =
      sessionStorage.getItem("manualOpenInMidResolution") === "true";

    if (currentWidth <= 768) {
      setCloseTab(false);
    } else if (currentWidth > 768 && currentWidth < 992) {
      if (manualOpen) {
        setCloseTab(false);
      } else {
        setCloseTab(true);
        localStorage.setItem("closeTab", JSON.stringify(true));
      }
    } else {
      if (savedState !== null) {
        setCloseTab(JSON.parse(savedState));
      } else {
        setCloseTab(false);
      }
    }
  }, [setCloseTab]);

  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const newIsMobileView = currentWidth <= 768;
      if (isPopupVisible && lastWidthRef.current > 992 && currentWidth <= 992) {
        setPopupVisible(false);
      }
      setIsMobileView(newIsMobileView);
      if (currentWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }

      const manualOpenInMidResolution =
        sessionStorage.getItem("manualOpenInMidResolution") === "true";

      if (newIsMobileView) {
        if (closeTab) {
          setCloseTab(false);
        }
      } else if (currentWidth > 768 && currentWidth < 992) {
        if (!manualOpenInMidResolution) {
          if (!closeTab) {
            setCloseTab(true);
            localStorage.setItem("closeTab", JSON.stringify(true));
          }
        } else {
          if (closeTab) {
            setCloseTab(false);
          }
        }
      } else {
        const savedState = localStorage.getItem("closeTab");
        if (savedState !== null) {
          const newCloseTabState = JSON.parse(savedState);
          if (closeTab !== newCloseTabState) {
            setCloseTab(newCloseTabState);
          }
        } else {
          if (closeTab) {
            setCloseTab(false);
          }
        }
        sessionStorage.removeItem("manualOpenInMidResolution");
      }
      lastWidthRef.current = currentWidth;
    };

    window.addEventListener("resize", handleResize);
    lastWidthRef.current = window.innerWidth;

    return () => window.removeEventListener("resize", handleResize);
  }, [mobileMenuOpen, setCloseTab, closeTab, isPopupVisible, userDetails]);

  useEffect(() => {
    const pathname = location.pathname;
    const mapping = [
      ["/dashboard", "Dashboard"],
      ["/user-recent-tracks", "Dashboard"],
      ["/daily-reports", "Daily Reports"],
      ["/resume-parser", "Resume Parser"],
      ["/job-description", "Job Description Generator"],
      ["/members", "Members"],
      ["/conversations", "Conversations"],
      ["/invoice-list", "Invoices"],
      ["/conversation", "Conversations"],
      ["/candidates-list", "Candidates"],
      ["/clients-list", isHiringTeamTenant ? "Hiring Team" : "Clients"],
      ["/vendors", "Vendors"],
      ["/jobs-list", "Jobs"],
      ["/workspace", "Workspace"],
      ["/interview-schdule", "Interviews"],
      ["/dice-search", "Vendors"],
      ["/monster-search-ind", "Vendors"],
      ["/monster-search-usa", "Vendors"],
      ["/irmt-search", "Vendors"],
      ["/notifications", "Notifications"],
    ];
    const found = mapping.find(([path]) => pathname.includes(path));
    setActiveLink(found ? found[1] : "Dashboard");
  }, [location]);

  useLayoutEffect(() => {
    if (showUploadInterruptPopup) {
      setPopupVisible(false);
      setMobileMenuOpen(false);
    }
  }, [showUploadInterruptPopup]);

  const footerMenuClass =
    isMobileView && mobileMenuOpen
      ? "mobile-footer-menu"
      : closeTab && !isMobileView
        ? "closed-footer-menu"
        : "footer-menu";

  const footerMenuElement = useMemo(() => {
    const forSpeedDial = closeTab && !isMobileView && !mobileMenuOpen;

    return (
      <div
        ref={footerMenuRef}
        className={footerMenuClass}
        onClick={!closeTab || isMobileView ? togglePopup : undefined}
      >
        {forSpeedDial && userDetails ? (
          <SpeedDial
            ariaLabel="User Actions"
            sx={{
              position: "relative",
              ".MuiSpeedDial-fab": {
                bgcolor: "#6860ee",
                "&:hover": { bgcolor: deepPurple[700] },
                width: "40px",
                height: "40px",
              },
              boxShadow: "none",
            }}
            icon={
              <Avatar
                sx={{ bgcolor: "#6860ee" }}
                src={userDetails?.profile_image_key}
              >
                {userDetails?.first_name?.[0]?.toUpperCase()}
              </Avatar>
            }
            direction="up"
            onClick={(e) => e.stopPropagation()}
          >
            {popupItems
              .slice()
              // .reverse()
              .map((item) => (
                <SpeedDialAction
                  key={item.name}
                  icon={item.speedDialIcon}
                  tooltipTitle={item.name}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.eventHandler();
                  }}
                />
              ))}
          </SpeedDial>
        ) : userDetails ? (
          <>
            <Avatar
              sx={{ bgcolor: "#6860ee" }}
              src={userDetails?.profile_image_key}
            >
              {userDetails?.first_name?.[0]?.toUpperCase()}
            </Avatar>
            <div className="username">
              {applyEllipsis(userDetails?.first_name || "User", 10)}
            </div>
            <MoreHorizIcon className="more-horiz-icon" onClick={togglePopup} />
            {isPopupVisible && (
              <DropDownMenu
                popupItems={popupItems}
                menuOpen={isPopupVisible}
                onClose={() => setPopupVisible(false)}
                anchorEl={footerMenuRef.current}
                customClass={
                  mobileMenuOpen
                    ? "mobile-avatar-dropdown"
                    : "desktop-avatar-dropdown"
                }
              />
            )}
          </>
        ) : null}
      </div>
    );
  }, [
    footerMenuClass,
    closeTab,
    isMobileView,
    mobileMenuOpen,
    togglePopup,
    userDetails,
    isPopupVisible,
    popupItems,
    setPopupVisible,
    footerMenuRef,
  ]);

  return (
    <>
      <div className="mobile-top-bar">
        <div
          className="mobile-menu-toggle-icon-container"
          onClick={toggleMobileMenu}
        >
          <img
            src={isDarkMode ? TabOpenDarkIcon : TabOpenIcon}
            alt="Menu"
            className="mobile-menu-icon"
          />
        </div>
        <img
          src={isDarkMode ? vybogTALDarkLogo : VylabsIcon}
          alt="Logo"
          className="mobile-top-bar-logo"
          onClick={() => {
            navigate("/dashboard");
            setMobileMenuOpen(false);
          }}
        />
      </div>

      <div
        className={`mobile-menu-overlay ${mobileMenuOpen ? "visible" : ""}`}
        onClick={toggleMobileMenu}
      ></div>

      <div
        className={`side-navbar-container ${
          isMobileView
            ? mobileMenuOpen
              ? "mobile-menu-expanded"
              : ""
            : closeTab
              ? "desktop-closed-tab"
              : "desktop-open-tab"
        }`}
      >
        <div
          className={`side-nav ${
            mobileMenuOpen ? "mobile-visible" : "mobile-hidden"
          }`}
        >
          <div
            className={
              closeTab && !isMobileView
                ? "single-logo-container"
                : "logo-container"
            }
          >
            <img
              src={
                closeTab && !isMobileView
                  ? VylabsAloneIcon
                  : isDarkMode
                    ? vybogTALDarkLogo
                    : VylabsIcon
              }
              alt="Logo"
              className={
                closeTab && !isMobileView ? "navbar-single-logo" : "navbar-logo"
              }
              onClick={() => {
                setPendingNavigationPath("/dashboard");
                // setShowUploadInterruptPopup(true);
                navigate("/dashboard");
              }}
            />
          </div>

          <div className="sidebar-main-container">
            <div className="menu">
              <NestedList
                items={filteredMenuItems}
                activeLink={activeLink}
                setActiveLink={setActiveLink}
                closeTab={closeTab && !isMobileView}
                onLinkClick={handleLinkClick}
                triggerNames={filteredMenuItems.map((item) => item.name)}
              />
            </div>
          </div>
          {footerMenuElement}
        </div>
        {!isMobileView && (
          <img
            src={
              closeTab
                ? isDarkMode
                  ? TabOpenDarkIcon
                  : TabOpenIcon
                : isDarkMode
                  ? TabCloseDarkIcon
                  : TabCloseIcon
            }
            alt="Toggle"
            className={closeTab ? "tab-open-icon" : "tab-close-icon"}
            onClick={handleTabChange}
          />
        )}
      </div>
    </>
  );
}

export default Navbar;
